<template>
  <div class="project" >

    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
          <img id="navbar-logo" src="../../assets/projects/solid/solid.jpg" alt="Solid Logo">
      </div>
    </nav>

    <h1 class="title" id="top">Internship @Greenhouse Group Labs - "SoLiD" Exploration</h1>

    <p>
      This year marked the thirtieth anniversary of the web’s inception, yet
      Tim Berners-Lee expressed disappointment in its trajectory, deeming it
      mishandled. He unveiled "Solid," an intriguing initiative signifying "Social
      Linked Data."
    </p>
    <p>
      Solid introduces user pods that store profile and app data. These pods
      communicate with specific apps, facilitating data reading and writing
      through RDF and turtle doc formats. The strength of RDF lies in its logical
      programming, enabling multiple applications to utilize shared data and
      resources like schemas.
    </p>
    <p>
      Greenhouse presented the challenge of developing prototypes on Solid,
      aiming to enhance the company’s operations and gain insights post the
      cookie apocalypse. In this endeavor, I worked extensively with React and
      SoLiD web 3.0 technologies. I crafted three prototypes: a blog for my
      internship teachers, a web3.0 iteration of WhatsApp, and a tool enabling
      users to personalize ads by selecting IAB categories of interest.
    </p>

    <h2>Solid Blog Prototype: User-Centric Blogging</h2>
    <p>
      The Solid Blog prototype showcases a blogging application built on Solid. It emphasizes user-centric control, allowing users to save and display blog posts in their user pods.
    </p>
    <br>
    <div v-highlight >
      <pre class="language-javascript">
        <code>
          //check user session
          solid.auth.trackSession(session => {
            if (!session) {
              document.querySelector("#logoutBtn").style.display = "none";
            } else {
              document.querySelector("#username").innerHTML = session.webId;
              document.querySelector("#loginBtn").style.display = "none";
              document.querySelector("#create").style.display = "block";
            }
          });

          //log in
            async function popupLogin() {
              let session = await solid.auth.currentSession();
              let popupUri = "https://inrupt.net/common/popup.html";
              if (!session) session = await solid.auth.popupLogin({
                popupUri
              });
              document.querySelector("#username").innerHTML = session.webId;
            }

            //Reading and writing data from/to dhurkmans pod
            solid.auth.fetch("https://dhurkmans.inrupt.net/public/blog/blog.txt").then(response => {
              if (response.status == 200) {
                response.json().then(json => {
                  mydata = json;
                  load();
                });
              }
            });

            //Load data locally
            function load() {
              document.querySelector("#posts").innerHTML = "";
            
              for (row of mydata) {
                var newElement = document.createElement("div");
                newElement.classList.add("box");
            
                var paragraph = document.createElement("p");
                var title = document.createElement("h1");
                var path = document.createElement("img");
            
                title.innerHTML = row.title;
                paragraph.innerHTML = row.text;
                path.src = row.path;
            
                newElement.appendChild(title);
                newElement.appendChild(paragraph);
                newElement.appendChild(path);
                document.querySelector("#posts").appendChild(newElement);
              }
            }

            //Write data locally
            function push() {
              mydata.push({
                title: document.querySelector("#addTitle").value,
                text: document.querySelector("#addText").value,
                path: document.querySelector("#addPath").value
              });
              load();
              solid.auth.fetch("https://dhurkmans.inrupt.net/public/blog/blog.txt", {
                method: "POST",
                body: JSON.stringify(mydata)
              });
            }

        </code>
      </pre>
    </div>
    <br>
    <p>
      The code snippets illustrate functionalities like checking user sessions, logging in, reading and writing data, and displaying blog posts.
    </p>
    <br>
   

    <img src="../../assets/projects/solid/blogimg.png" alt="">

 <br>
  
    <h2>Solid Chat Prototype: Decentralized Messaging</h2>
  
    <p>
      The Solid Chat prototype offers a messaging platform for Solid users, utilizing websockets for near real-time communication. It explains the structure of conversations in RDF format and how messages are exchanged between users.
    </p>
    <br>
    <div v-highlight >
      <pre class="language-javascript">
        <code>
            import { createDocument, fetchDocument, getFetcher } from "tripledoc";
            import { space, rdf, solid, schema, ldp } from "rdf-namespaces";
            import auth from "solid-auth-client";
            import { NamedNode} from "rdflib";
            const SolidAclUtils = require("solid-acl-utils");
            const { AclApi, Permissions } = SolidAclUtils;
            const { READ } = Permissions;
        </code>
      </pre>
    </div>
    <br>
    <div v-highlight >
      <pre class="language-javascript">
        <code>
           // Check inbox
            export async function createLinkFromInbox(profile) {
              const inboxList = profile.getNodeRef(ldp.inbox);
              const inbox = await fetchDocument(inboxList);
              const inboxSubject = inbox.getSubject(inboxList);
              const inboxFiles = inboxSubject.getAllNodeRefs(ldp.contains);
              const fetcher = getFetcher();
              const chatsFile = await getChatList(profile);
              const chats = chatsFile.getSubjectsOfType(schema.Conversation);
            
              for (const uri of inboxFiles) {
                const file = await fetchDocument(uri);
                const conversations = file.getSubjectsOfType(schema.Conversation);
                let created = false;
                for (const conversation of conversations) {
                  const theirWebId = conversation.getNodeRef(schema.toRecipient);
                  const file = conversation.getNodeRef(schema.discussionUrl);
            
                  for (const chat of chats) {
                    if (chat.getNodeRef(schema.toRecipient) === theirWebId) {
                      chat.setNodeRef(schema.discussionUrl, file);
                      created = true;
                    }
                  }
            
                  if (!created) {
                    const chat = chatsFile.addSubject();
                    chat.setNodeRef(rdf.type, schema.Conversation);
                    chat.setNodeRef(schema.toRecipient, theirWebId);
                    chat.setNodeRef(schema.author, profile.asNodeRef());
                    chat.setNodeRef(schema.discussionUrl, file);
                    created = true;
                  }
                }
                if (created) {
                  await fetcher.delete(uri);
                }
              }
              await chatsFile.save();
            }
        </code>
      </pre>
    </div>
    <br>
      <div v-highlight >
      <pre class="language-javascript">
        <code>
              export async function getContacts(profile) {
              const chatList = await getChatList(profile);
              const chats = chatList.getSubjectsOfType(schema.Conversation);
            
              let contacts = [];
            
              for (const c of chats) {
                const recipient = c.getNodeRef(schema.toRecipient);
                if (recipient) {
                  contacts.push(recipient);
                }
              }
              return contacts;
            }
        </code>
      </pre>
    </div>
    <br>
      <div v-highlight >
      <pre class="language-javascript">
        <code>
            // Stuur link bericht -> inbox
            export async function sendRequestToInbox(profile, theirWebId, conversationUrl) {
              // get friends inbox location
              const theirProfileDoc = await fetchDocument(theirWebId);
              const theirProfile = theirProfileDoc.getSubject(theirWebId);
              const theirInbox = theirProfile.getNodeRef(ldp.inbox);
            
              // get value from form in sidebar.jsx
              <!-- const message = `@prefix : <#>.
                :conversation a ;
                 <${profile.asNodeRef()}> ;+
                
                 <${conversationUrl}> .
                `; -->
            
              const fetcher = getFetcher();
              await fetcher.webOperation("POST", theirInbox, {
                body: message,
                contentType: "text/turtle"
              });
            }  
        </code>
      </pre>
    </div>
    <br>
      <div v-highlight >
      <pre class="language-javascript">
        <code>
            // create chats doc in privateTypeIndex
            export async function initialiseChatList(profile, typeIndex) {
              const storage = profile.getNodeRef(space.storage);
              const chatListUrl = storage + "chat/chats.ttl";
            
              const chatList = createDocument(chatListUrl);
              await chatList.save();
            
              const typeRegistration = typeIndex.addSubject({
                identifier: "chatConversations"
              });
              typeRegistration.addNodeRef(rdf.type, solid.TypeRegistration);
              typeRegistration.addNodeRef(solid.instance, chatList.asNodeRef());
              typeRegistration.addNodeRef(solid.forClass, schema.Conversation);
              await typeIndex.save([typeRegistration]);
            
              return chatList;
            }
        </code>
      </pre>
    </div>
    <br>
      <div v-highlight >
      <pre class="language-javascript">
        <code>
                // get conversation from chatlist
                export async function getConversation(profile, theirWebId) {
                  const chatList = await getChatList(profile);
                  const chats = chatList.getSubjectsOfType(schema.Conversation);
                  let chat = null;
                
                  for (const c of chats) {
                    if (c.getNodeRef(schema.toRecipient) === theirWebId) {
                      if (!c.getNodeRef(schema.url)) {
                        const file = await addConversationFile(profile, theirWebId);
                        c.setNodeRef(schema.url, file.asNodeRef());
                        await chatList.save();
                      }
                      chat = c;
                    }
                  }
                
                  if (!chat) chat = await addConversation(profile, theirWebId, chatList);
                
                  return chat;
                }  
        </code>
      </pre>
    </div>
    <br>
          <div v-highlight >
      <pre class="language-javascript">
        <code>
            // add conversation to chatlist
            export async function addConversationFile(profile, theirWebId) {
              const storage = profile.getNodeRef(space.storage);
              const newChatUrl = storage + "chat/chats/";
              const chatName = Date.now() + Math.floor(Math.random() * 1000 + 123) + ".ttl";
            
              const chat = createDocument(newChatUrl + chatName);
              await chat.save();
            
              const fetch = auth.fetch.bind(auth);
              const aclApi = new AclApi(fetch, {
                autoSave: false
              });
              const acl = await aclApi.loadFromFileUrl(newChatUrl + chatName);
            
              if (!acl.hasRule(Permissions.ALL, profile.asNodeRef())) {
                await acl.addRule(Permissions.ALL, profile.asNodeRef());
              }
            
              await acl.addRule(READ, theirWebId);
              await acl.saveToPod();
            
              await sendRequestToInbox(profile, theirWebId, chat.asNodeRef());
            
              return chat;
            }
        </code>
      </pre>
    </div>
    <br>
          <div v-highlight >
      <pre class="language-javascript">
        <code>
            // add conversation to chatlist
            export async function addConversation(profile, theirWebId, chatList) {
              const chat = await addConversationFile(profile, theirWebId);
            
              const conversation = chatList.addSubject();
              conversation.setNodeRef(rdf.type, schema.Conversation);
              conversation.setNodeRef(schema.author, profile.asNodeRef());
              conversation.setNodeRef(schema.url, chat.asNodeRef());
              conversation.setNodeRef(schema.toRecipient, theirWebId);
            
              await chatList.save();
            
              return conversation;
            }  
        </code>
      </pre>
    </div>
    <br>
          <div v-highlight >
      <pre class="language-javascript">
        <code>
            // Add message
            export async function addMessageToRightConversation(
              profile,
              message,
              theirWebId
            ) {
              const chatLink = await getConversation(profile, theirWebId);
              const chat = await fetchDocument(chatLink.getNodeRef(schema.url));
              const newMessage = chat.addSubject();
              newMessage.addNodeRef(rdf.type, schema.Message);
              newMessage.addLiteral(schema.text, message);
              newMessage.addNodeRef(schema.author, profile.asNodeRef());
              newMessage.addLiteral(schema.dateCreated, Date.now());
              await chat.save();
            }
        </code>
      </pre>
    </div>
    <br>
          <div v-highlight >
      <pre class="language-javascript">
        <code>
            // Open chat from pod
            export async function fetchChatFromPods(profile, theirWebId) {
              const chatsFile = await getChatList(profile);
              const chats = chatsFile.getSubjectsOfType(schema.Conversation);
              const messages = [];
              for (const chat of chats) {
                if (chat.getNodeRef(schema.toRecipient) === theirWebId) {
                  const fetcher = getFetcher();
                  const mine = chat.getNodeRef(schema.url);
                  if (mine) {
                    await fetcher.load(mine, {
                      force: true
                    });
                    const myDocument = await fetchDocument(mine);
                    const myMessages = myDocument.getSubjectsOfType(schema.Message);
                    myMessages.forEach(m => {
                      messages.push({
                        text: m.getLiteral(schema.text),
                        dateCreated: m.getLiteral(schema.dateCreated),
                        author: m.getNodeRef(schema.author)
                      });
                    });
                  }
                  const theirs = chat.getNodeRef(schema.discussionUrl);
                  if (theirs) {
                    await fetcher.load(theirs, {
                      force: true
                    });
                    const theirDocument = await fetchDocument(theirs);
                    const theirMessages = theirDocument.getSubjectsOfType(schema.Message);
                    theirMessages.forEach(m => {
                      messages.push({
                        text: m.getLiteral(schema.text),
                        dateCreated: m.getLiteral(schema.dateCreated),
                        author: m.getNodeRef(schema.author)
                      });
                    });
                  }
                }
              }
              const sortedMessages = messages.sort((a, b) => {
                return a.dateCreated - b.dateCreated;
              });
              // console.log(messages);
              return sortedMessages;
            }
        </code>
      </pre>
    </div>
    <br>
      <div v-highlight >
      <pre class="language-javascript">
        <code>
            // Check if PTI exists
            export async function getChatList(profile) {
              const privateTypeIndexUrl = profile.getNodeRef(solid.privateTypeIndex);
              const privateTypeIndex = await fetchDocument(privateTypeIndexUrl);
              const chatListEntry = privateTypeIndex.findSubject(
                solid.forClass,
                schema.Conversation
              );
            
              if (chatListEntry === null) {
                return initialiseChatList(profile, privateTypeIndex);
              }
            
              const chatListUrl = chatListEntry.getNodeRef(solid.instance);
              return await fetchDocument(chatListUrl);
            } 
               
        </code>
      </pre>
    </div>
    <br>
          <div v-highlight >
      <pre class="language-javascript">
        <code>
              export async function getWebSocketUrl(theirFile) {
              const theirsNode = new NamedNode(theirFile);
              const fetcher = getFetcher();
              await fetcher.load(theirsNode);
              const header = fetcher.getHeader(theirsNode, "Updates-Via");
              if (header) {
                return header[0];
              }
            }
        </code>
      </pre>
    </div>

      <!-- <div v-highlight >
      <pre class="language-javascript">
        <code>
          // insert code
        </code>
      </pre>
    </div> -->
    <br>

    <p>
      The code snippets demonstrate key functionalities, such as checking user sessions, logging in, reading and writing data, and handling chat interactions.
    </p>

    <br>
    <img src="../../assets/projects/solid/chatimg.png" alt="">
  <br>
    <h2>Solid IAB Schema Generator: Transforming IAB Data</h2>
  
    <p>
      The IAB Schema Generator is a compact tool for converting IAB's JSON data into RDF format. The JSON file includes ad categories, and this tool creates an RDF ontology for each category. The resulting RDF schema is stored in the user's Solid pod, where it can be used in subsequent applications.
    </p>
    <br>

    <div v-highlight >
      <pre class="language-javascript">
        <code>
          import { createDocument } from "tripledoc";
          import { rdf, owl, rdfs } from "rdf-namespaces";
        </code>
      </pre>
    </div>
    <br> 
    <div v-highlight >
      <pre class="language-javascript">
        <code> 
          export async function createDocRDF() {
              // create a new doc/file in a open pod folder
              const newDocURL =
                "https://testperson123.solid.community/TestSchemas/" + "categories.ttl";
            
              // Create the new Document:
              const newDoc = createDocument(newDocURL);
            
              // add NodeReferences to: *ID  * Name  * Child of  * Childeren   // and define them in the header of the turtle file
              var id = newDoc.addSubject({ identifier: "id" });
              id.addNodeRef(rdf.type, rdf.Property);
              id.addNodeRef(rdfs.isDefinedBy, newDoc.asNodeRef());
              id.addLiteral(rdfs.label, "ID");
              id.addLiteral(rdfs.comment,"This is the ID that has been given to the category.");
            
              var name = newDoc.addSubject({ identifier: "name" });
              name.addNodeRef(rdf.type, rdf.Property);
              name.addNodeRef(rdfs.isDefinedBy, newDoc.asNodeRef());
              name.addLiteral(rdfs.label, "Name");
              name.addLiteral(rdfs.comment, "This is the name of the category.");
            
              var children = newDoc.addSubject({ identifier: "subCategories" });
              children.addNodeRef(rdf.type, rdf.Property);
              children.addNodeRef(rdfs.isDefinedBy, newDoc.asNodeRef());
              children.addLiteral(rdfs.label, "Subcategories");
              children.addLiteral(rdfs.comment, "The children of this category.");
            
              var childOf = newDoc.addSubject({ identifier: "subCategoryOf" });
              childOf.addNodeRef(rdf.type, rdf.Property);
              childOf.addNodeRef(rdfs.isDefinedBy, newDoc.asNodeRef());
              childOf.addLiteral(rdfs.label, "SubcategoryOf");
              childOf.addLiteral(rdfs.comment, "The caterory obove the current child.");
            
              var IABRef = newDoc.addSubject({ identifier: "IAB" });
              IABRef.addNodeRef(rdf.type, rdfs.Class);
              IABRef.addNodeRef(rdfs.isDefinedBy, newDoc.asNodeRef());
              IABRef.addLiteral(rdfs.label, "IAB");
              IABRef.addLiteral(rdfs.comment, "This is the base category.");
            
              // read the json file and log the list
              const myJson = await fetch("categories.json");
              const categoryList = await myJson.json();
            
              // for item in categories.json create a triple NodeReferencing the previous topics
              categoryList.forEach((category, index) => {
                var categoryId = Object.keys(category)[0];
                var categoryIdArray = categoryId.split(".");
                var categoryName = category;
                <!-- for (var i = 0; i < categoryIdArray.length; i++) {
                  categoryName = Object.values(categoryName)[0];
                } -->
                var subjectUri = `IAB-${categoryIdArray.join("-")}`;
                var parentUri =
                  categoryIdArray.length !== 1
                    ? `IAB-${categoryIdArray.slice(0, -1).join("-")}`
                    : null;
            
                var childs = [];
                var searchChildren = true;
                var i = 1;
                while (searchChildren) {
                  var child = categoryList[index + i];
                  if (child) {
                    var childId = Object.keys(child)[0];
                    var childIdArray = childId.split(".");
                    var childParentId = childIdArray.slice(0, -1).join(".");
                    var childUri = `IAB-${childIdArray.join("-")}`;
                    if (childParentId === categoryId) {
                      childs.push(childUri);
                    } else if (categoryIdArray[0] === childIdArray[0]) {
                      //   console.log("ignore this boi");
                    } else {
                      searchChildren = false;
                    }
                    i++;
                  } else {
                    searchChildren = false;
                  }
                }
            
                // + write them to the ttl document
                var categoryItem = newDoc.addSubject({ identifier: subjectUri });
                categoryItem.addNodeRef(rdf.type, rdfs.Class);
                categoryItem.addNodeRef(rdfs.subClassOf, IABRef.asNodeRef());
                categoryItem.addLiteral(id.asNodeRef(), categoryId);
                categoryItem.addLiteral(name.asNodeRef(), categoryName);
                for (var c of childs) {
                  categoryItem.addNodeRef(children.asNodeRef(), newDocURL + "#" + c);
                }
                if (categoryIdArray.length !== 1) {
                  categoryItem.addNodeRef(childOf.asNodeRef(), newDocURL + "#" + parentUri);
                }
              });
              await newDoc.save();
            }
        </code>
      </pre>
    </div>
  <br>
    <br>
    <img src="../../assets/projects/solid/AP1img.png" alt="">
    <br>


    <h2>Solid AdPicking Prototype: Customizing Your Ad Experience</h2>
  
    <p>
      The AdPicking prototype offers users the ability to personalize their ad experience. By subscribing to categories of interest, users can specify the types of ads they want to see. The project revolves around a user interface, where individuals can select their preferred ad categories, and these choices are saved in their Solid pod. The categories stored in the pod facilitate the system's ability to display relevant ads and filter out unwanted ones.
    </p>
    <br>

    <div v-highlight >
      <pre class="language-javascript">
        <code>
          import { createDocument, fetchDocument } from "tripledoc";
          import { space, rdf, solid} from "rdf-namespaces";
        </code>
      </pre>
    </div>
    <br>

    <div v-highlight >
      <pre class="language-javascript">
        <code>
          // Create PTI for categories
            export async function initialiseInterests(profile, typeIndex) {
              const storage = profile.getNodeRef(space.storage);
              const categoriesURL = storage + "adpicker/categories.ttl";
            
              const categoriesProfile = createDocument(categoriesURL);
              await categoriesProfile.save();
            
              const typeRegistration = typeIndex.addSubject({
                identifier: "adPickingInterests"
              });
              typeRegistration.addNodeRef(rdf.type, solid.TypeRegistration);
              typeRegistration.addNodeRef(solid.instance, categoriesProfile.asNodeRef());
              typeRegistration.addNodeRef(
                solid.forClass,
                "https://testperson123.solid.community/TestSchemas/categories.ttl#IAB"
              );
              await typeIndex.save([typeRegistration]);
            
              return typeRegistration;
            
        </code>
      </pre>
    </div>
    <br>

    <div v-highlight >
      <pre class="language-javascript">
        <code>
          // Check if PTI exists
            export async function getInterestsFile(profile) {
              const privateTypeIndexUrl = profile.getRef(solid.privateTypeIndex);
              const privateTypeIndex = await fetchDocument(privateTypeIndexUrl);
              let interestsListEntry = privateTypeIndex.findSubject(
                solid.forClass,
                "https://testperson123.solid.community/TestSchemas/categories.ttl#IAB"
              );
            
              if (interestsListEntry === null) {
                interestsListEntry = await initialiseInterests(profile, privateTypeIndex);
              }
              const interestsListUrl = interestsListEntry.getRef(solid.instance);
              return await fetchDocument(interestsListUrl);
            }
        </code>
      </pre>
    </div>
    <br>

    <div v-highlight >
      <pre class="language-javascript">
        <code>
            import { fetchDocument } from 'tripledoc';
            import { rdf, rdfs, schema } from "rdf-namespaces";
            import { Name } from 'rdf-namespaces/dist/vcard';
            import { where } from 'rdf-namespaces/dist/solid';
            import { subClassOf } from 'rdf-namespaces/dist/rdfs';
            import { getInterestsFile } from "../data/podStorage";

             
            // Show all categories
            export async function listAllMainCategories() {
              const link = "https://testperson123.solid.community/TestSchemas/categories.ttl";
              const categories = await fetchDocument(link);
              const categoryList = categories.getSubjectsOfType(rdfs.Class);

              const mainCategories = categoryList.filter((category) => {
                return category.getNodeRef(rdfs.subClassOf) === link + "#IAB";
              }).filter((category) => {
                return category.getNodeRef(link + '#subCategoryOf') === null;
              }).map(category => {
                return {
                  uri: category.asRef(),
                  name: category.getLiteral(link + "#name")
                };
              });
              return mainCategories
            }

            // delete a category from categories.tll
            export async function removeCategoryFromPod(profile, category) {
              const doc = await getInterestsFile(profile);
              doc.getSubjectsOfType(category.uri).forEach(subject => {
                doc.removeSubject(subject.asRef());
              });
            
              const success = await doc.save();
              return success;
            }

            // add a category to categories.tll
            export async function addCategoryToPod(profile, category) {
              const doc = await getInterestsFile(profile); //replace for function in podStorage.js
              const newCategory = doc.addSubject();
              newCategory.addRef(rdf.type, "https://testperson123.solid.community/TestSchemas/categories.ttl#IAB");
              newCategory.addRef(rdf.type, category.uri);
              const success = await doc.save([newCategory]);
              return success;
            }

            // check what categories are saved in the pod
          export async function getListOfCategoriesFromPod(profile) {
            const doc = await getInterestsFile(profile);
            const subscribeList = [];
            const myCategories = doc.getSubjectsOfType("https://testperson123.solid.community/TestSchemas/categories.ttl#IAB");
            myCategories.forEach(c => {
              subscribeList.push(c.getAllRefs(rdf.type).filter(r => {
                return r !== "https://testperson123.solid.community/TestSchemas/categories.ttl#IAB"
              })[0]);
            });
            return subscribeList;
        </code>
      </pre>
    </div>
    <br>


    <p>
      These functions collectively allow users to manage their ad preferences, selecting the categories they are interested in and controlling the types of ads they see. The user's choices are stored in their Solid pod, ensuring that the system delivers personalized advertising experiences.
    </p>
    <br>

    <img src="../../assets/projects/solid/AP2img.png" alt="">
    <br>


    <h2>Web 2.0 vs. 3.0: The Evolution of the Web</h2>
    <p>
      This article discusses the transition from Web 2.0 to Web 3.0, highlighting the fundamental changes in web architecture, data handling, and user experiences. It explores the shift from centralized data in Web 2.0 to more decentralized and user-controlled data in Web 3.0.
    </p>
    <br>

    <img src="../../assets/projects/solid/solidImg.png" alt="">
    <br>


    <h2>Solid x Greenhouse Group: Innovating with Solid</h2>
    <p>
      This piece explores Solid, an initiative led by Tim Berners-Lee, the inventor of the World Wide Web. It outlines the concept of Solid, which involves user pods to save profile and app data, enabling decentralized data control and access. Greenhouse Group's interest in building prototypes on Solid to improve their targeting capabilities and adapt to a post-cookie apocalypse is also discussed.
    </p>

  
    <h2>Solid AdPicking Prototype: Navigating Ads with control</h2>
    <p>
      The AdPicking prototype offers users the ability to customize their ad experience. Users can select the ad categories they are interested in, and their choices are stored in a Solid pod. This data enables the system to display relevant ads while excluding undesired ones.
    </p>

    <br>

    <video controls>
      <source src="../../assets/projects/solid/test.mp4" type="video/mp4">
    </video> 

  </div>
</template>


<style scoped>
  
@import 'project.css';

</style>